import React, {
  useState,
  lazy,
  Suspense,
  useCallback,
} from "react";

import {
  useNavigate,
  useLocation,
  Link,
} from "react-router-dom";

import { useAuth } from "../components/AuthContext";

import { useCart } from "../components/CartContext";

import { toast } from "react-toastify";

import "../styles/Auth.css";

/* =========================================
   GOOGLE LOGIN LAZY LOAD
========================================= */

const GoogleLogin = lazy(() =>
  import("@react-oauth/google").then(
    (module) => ({
      default: module.GoogleLogin,
    })
  )
);

const LoginPage = () => {

  const navigate = useNavigate();

  const location = useLocation();

  const {
    login,
    googleLogin,
    forgotPassword,
    resetPassword,
  } = useAuth();

  const { cart } = useCart();

  /* =========================================
     STATES
  ========================================= */

  const [form, setForm] =
    useState({

      email: "",

      password: "",
    });

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [forgotMode, setForgotMode] =
    useState(false);

  const [otpStage, setOtpStage] =
    useState(false);

  const [emailForReset, setEmailForReset] =
    useState("");

  const [otp, setOtp] =
    useState("");

  const [newPassword, setNewPassword] =
    useState("");

  /* =========================================
     REDIRECT
  ========================================= */

  const getRedirectPath = () => {

    if (
      location.state?.from?.pathname
    ) {

      return location.state.from.pathname;
    }

    if (
      cart &&
      cart.length > 0
    ) {

      return "/cart";
    }

    return "/";
  };

  /* =========================================
     INPUT CHANGE
  ========================================= */

  const handleChange = useCallback(
    (e) => {

      setForm((prev) => ({

        ...prev,

        [e.target.name]:
          e.target.value,
      }));

      setError("");

    },
    []
  );

  /* =========================================
     LOGIN
  ========================================= */

  const handleSubmit =
    useCallback(
      async (e) => {

        e.preventDefault();

        if (
          !form.email ||
          !form.password
        ) {

          setError(
            "Please enter email and password"
          );

          return;
        }

        setLoading(true);

        setError("");

        try {

          const {
            success,
            message,
          } = await login(
            form.email,
            form.password
          );

          if (success) {

            /* META */

            if (window.fbq) {

              window.fbq(
                "track",
                "Login",
                {
                  method: "email",
                }
              );
            }

            toast.success(
              "Login successful"
            );

            navigate(
              getRedirectPath(),
              {
                replace: true,
              }
            );

          } else {

            const msg =
              message?.toLowerCase() ||
              "";

            if (
              msg.includes("verify") ||
              msg.includes("otp")
            ) {

              setError(
                "OTP sent to your email. Please verify."
              );

              setEmailForReset(
                form.email
              );

              setForgotMode(true);

            } else if (
              msg.includes(
                "not registered"
              ) ||
              msg.includes(
                "not found"
              )
            ) {

              setError(
                "Email is not registered"
              );

            } else if (
              msg.includes(
                "password"
              )
            ) {

              setError(
                "Incorrect password"
              );

            } else if (
              msg.includes(
                "google"
              )
            ) {

              setError(
                "Please login using Google"
              );

            } else {

              setError(
                message ||
                "Login failed"
              );
            }
          }

        } catch (err) {

          console.error(err);

          setError(
            "Server error. Try again."
          );

        } finally {

          setLoading(false);
        }
      },
      [
        form,
        login,
        navigate,
        cart,
        location,
      ]
    );

  /* =========================================
     GOOGLE LOGIN
  ========================================= */

  const handleGoogleSuccess =
    async (res) => {

      try {

        const result =
          await googleLogin(
            res.credential
          );

        if (result.success) {

          /* META */

          if (window.fbq) {

            window.fbq(
              "track",
              "Login",
              {
                method: "google",
              }
            );
          }

          toast.success(
            "Logged in with Google"
          );

          navigate(
            getRedirectPath(),
            {
              replace: true,
            }
          );

        } else {

          toast.error(
            result.message ||
            "Google login failed"
          );
        }

      } catch {

        toast.error(
          "Google login failed"
        );
      }
    };

  /* =========================================
     SEND OTP
  ========================================= */

  const handleSendOtp =
    async (e) => {

      e.preventDefault();

      if (
        !emailForReset.trim()
      ) {

        toast.warning(
          "Enter your registered email"
        );

        return;
      }

      setLoading(true);

      const res =
        await forgotPassword(
          emailForReset
        );

      setLoading(false);

      if (res.success) {

        /* META */

        if (window.fbq) {

          window.fbq(
            "track",
            "Lead",
            {
              content_name:
                "Forgot Password OTP",
            }
          );
        }

        toast.success(
          "OTP sent to your email"
        );

        setOtpStage(true);

      } else {

        toast.error(
          res.message
        );
      }
    };

  /* =========================================
     RESET PASSWORD
  ========================================= */

  const handleResetPassword =
    async (e) => {

      e.preventDefault();

      if (
        !otp ||
        !newPassword
      ) {

        toast.warning(
          "Fill all fields"
        );

        return;
      }

      setLoading(true);

      const res =
        await resetPassword(
          emailForReset,
          otp,
          newPassword
        );

      setLoading(false);

      if (res.success) {

        toast.success(
          "Password reset successful"
        );

        setForgotMode(false);

        setOtpStage(false);

        setOtp("");

        setNewPassword("");

      } else {

        toast.error(
          res.message
        );
      }
    };

  return (

    <div className="login-hero">

      {/* BACKGROUND */}

      <div className="auth-bg-overlay"></div>

      <img
        src="https://i.ibb.co/cS1d9nfp/sac-mtb-about-us-bicycle-nature.png"
        alt="SAC"
        className="auth-bg-image"
        loading="eager"
      />

      <div className="auth-gradient"></div>

      {/* HERO CONTENT */}

      <div className="login-hero-content">

        <h1>

          ENTER THE <br />
          SAC UNIVERSE

        </h1>

        <p>

          Explore futuristic bicycles,
          premium ride-on toys,
          gaming adventures and the
          next generation of mobility.

        </p>

      </div>

      {/* AUTH CARD */}

      <div className="auth-card">

        {!forgotMode ? (

          <>

            <h2>

              Login

            </h2>

            <form
              onSubmit={
                handleSubmit
              }
            >

              {/* EMAIL */}

              <input
                type="email"
                name="email"
                placeholder="Email address"
                value={form.email}
                onChange={
                  handleChange
                }
                className={
                  error
                    ? "error"
                    : ""
                }
                autoComplete="email"
                spellCheck="false"
                required
              />

              {/* PASSWORD */}

              <input
                type="password"
                name="password"
                placeholder="Password"
                value={form.password}
                onChange={
                  handleChange
                }
                className={
                  error
                    ? "error"
                    : ""
                }
                autoComplete="current-password"
                required
              />

              {/* ERROR */}

              {error && (

                <p className="error-text">

                  {error}

                </p>
              )}

              {/* LOGIN BTN */}

              <button
                type="submit"
                disabled={loading}
              >

                {loading
                  ? "Logging in..."
                  : "Login"}

              </button>

            </form>

            {/* LINKS */}

            <div className="login-helper">

              <span
                onClick={() =>
                  setForgotMode(true)
                }
              >

                Forgot Password?

              </span>

              <Link to="/register">

                Create Account

              </Link>

            </div>

            {/* DIVIDER */}

            <div className="login-divider">

              OR

            </div>

            {/* GOOGLE */}

            <div className="social-login">

              <Suspense
                fallback={
                  <div className="google-loader">

                    Loading Google Login...

                  </div>
                }
              >

                <GoogleLogin
                  onSuccess={
                    handleGoogleSuccess
                  }
                  onError={() =>
                    toast.error(
                      "Google login failed"
                    )
                  }
                />

              </Suspense>

            </div>

          </>

        ) : (

          <>

            <h2>

              {otpStage

                ? "Reset Password"

                : "Forgot Password"}

            </h2>

            {!otpStage ? (

              <form
                onSubmit={
                  handleSendOtp
                }
              >

                <input
                  type="email"
                  placeholder="Registered email"
                  value={emailForReset}
                  onChange={(e) =>
                    setEmailForReset(
                      e.target.value
                    )
                  }
                  required
                />

                <button
                  disabled={loading}
                >

                  {loading
                    ? "Sending OTP..."
                    : "Send OTP"}

                </button>

                <p
                  className="back-link"
                  onClick={() => {

                    setForgotMode(
                      false
                    );

                    setOtpStage(
                      false
                    );
                  }}
                >

                  Back to Login

                </p>

              </form>

            ) : (

              <form
                onSubmit={
                  handleResetPassword
                }
              >

                <input
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) =>
                    setOtp(
                      e.target.value
                    )
                  }
                  required
                />

                <input
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) =>
                    setNewPassword(
                      e.target.value
                    )
                  }
                  required
                />

                <button
                  disabled={loading}
                >

                  {loading
                    ? "Resetting..."
                    : "Reset Password"}

                </button>

                <p
                  className="back-link"
                  onClick={() => {

                    setForgotMode(
                      false
                    );

                    setOtpStage(
                      false
                    );
                  }}
                >

                  Back to Login

                </p>

              </form>

            )}

          </>

        )}

      </div>

    </div>
  );
};

export default LoginPage;